'use client';

import { useState } from 'react';
import {
    Button,
    Modal,
    TextInput,
    Textarea,
    Stack,
    Title,
    Group,
    Text,
    Box,
    Center,
    Tooltip,
} from '@mantine/core';
import { Dropzone } from '../EditorText/Dropzone';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';

export function GenerateDictionariesModal() {
    const [opened, setOpened] = useState(true);
    const [compagnyName, setCompagnyName] = useState('');
    const [description, setDescription] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [jsonBase, setJsonBase] = useState('');
    const [jsonBaseEN, setJsonBaseEN] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copiedEN, setCopiedEN] = useState(false);

    const allFieldsFilled = compagnyName && description && logoUrl ? true : false;

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/create-dictionaries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    compagnyName, description, logoUrl, ...(jsonBase ? { jsonBaseFr: jsonBase } : {}),
                    ...(jsonBaseEN ? { jsonBaseEn: jsonBaseEN } : {}),
                }),
            });

            const data = await res.json();

            if (res.ok) {
                addDeploymentToast('Déploiement du squelette en cours. Optimisation des fichiers...');
                setOpened(false);
            } else {
                alert(data.error || 'Erreur de génération');
            }
        } catch (error) {
            alert('Erreur serveur');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPrompt = async (locale: 'fr' | 'en') => {
        const prompt = locale === 'fr' ? `
        Génère un JSON complet pour une entreprise nommée "${compagnyName}" avec comme description: "${description}".
        Le logo est à l'URL: ${logoUrl}. Structure attendue. Ne change aucune key et garde ** EXACTEMENT ** la même structure (seules les values changent, pas les keys) : 
        ${jsonBase || `{"pages": {"home": {"link": "/", "label": "Accueil", "title": "Accueil", "slices": []}, "admin": {"title": "Votre Espace Administrateur", "addArticles": "Publier un Article", "addProducts": "Présenter une Réalisation", "description": "Bienvenue dans votre espace Administrateur, conçu pour une gestion efficace et intuitive. Nous avons mis à disposition trois outils clés pour dynamiser votre présence en ligne : l'ajout d'articles, l'ajout d'œuvres, et la gestion des estimations. Grâce à l'ajout d'articles, vous pourrez améliorer votre référencement SEO, augmenter votre visibilité et attirer plus de clients potentiels grâce à des publications régulières et de haute qualité. La page produits est votre vitrine pour présenter vos réalisations et évaluer l'intérêt pour la vente en ligne. En cas de demande croissante, l'intégration d'un système de paiement en ligne peut être aisément configurée. Voici vos options :", "manageEstimations": "Suivi des Devis"}, "contact": {"link": "/contact", "label": "Contactez", "title": "Contact", "slices": []}, "articles": {"title": "Découvrez tous nos articles & conseils", "search": "Vous cherchez un conseil d'expert ? Utilisez notre recherche !", "writeAlone": "Écrire l'article seul", "createTitle": "Créer un nouvel article", "writteAlone": "Écrire l'article seul", "titlePlaceholder": "Quel est le titre de l'article ?", "descriptionPlaceholder": "Courte description de l'article"}, "products": {"title": "Découvrez tous nos services", "search": "Trouvez les services de construction ou de rénovation dont vous avez besoin", "headerCard": {"src": "https://xrmxsojjgvlabymrbqbk.supabase.co/storage/v1/object/public/alabri-images//img-1745266037054.webp", "type": "horizontal-image-card", "title": "Explorez notre expertise en bâtiment", "position": "right", "description": "ALABRI BTP est votre partenaire de confiance pour tous vos projets de construction et de rénovation. Notre équipe qualifiée est prête à transformer vos idées en réalité avec un travail rapide, soigné et de qualité. De la conception à la réalisation, nous vous accompagnons sur toute l'île de France pour faire de votre espace un lieu unique. Découvrez nos réalisations et laissez-nous créer l'espace de vos rêves.", "backgroundGradient": "linear-gradient(to right, white, white)"}}, "estimation": {"all": "Tous", "cta": "Estimez vos travaux facilement", "date": "Soumis le", "open": "Ouvert", "label": "Devis de Travaux à Paris", "title": "Évaluez le Coût de Vos Travaux avec ALABRI BTP", "reopen": "Réouvrir", "search": "Gestion des devis", "slices": [], "status": "Statut :", "example": "Nos derniers chantiers", "resolve": "Archiver", "awaiting": "En attente", "noResult": "Aucun devis trouvé", "resolved": "Archivé", "searchPlaceholder": "Rechercher un nom, un numéro de téléphone ou un commentaire", "searchExplaination": "Utilisez la barre de recherche pour retrouver un devis par nom, commentaire ou numéro de téléphone. Filtrez par statut (reçu, en attente, archivé) et partagez les résultats grâce à l'URL. Les mises à jour s'effectuent automatiquement."}, "estimations": {"name": "Votre nom", "email": "Votre Email", "phone": "Votre numéro de téléphone", "comment": "Un commentaire ? (Optionnel)", "sending": "Envoi", "estimate": "Demander un devis"}}, "footer": {"links": [], "tiktok": "https://www.tiktok.com/@alabribtp", "twitter": "https://twitter.com/alabribtp", "facebook": "https://www.facebook.com/alabribtp", "copyright": "© 2024 Hestia Innovation. Tous droits réservés.", "instagram": "https://www.instagram.com/alabribtp", "description": "ALABRI BTP est une entreprise générale du bâtiment opérant dans tous les corps d'état pour des projets de rénovation et de construction sur toute l'île de France. Nous mettons un point d'honneur à fournir un travail rapide, soigné et de qualité pour satisfaire nos clients."}, "header": {"login": "Connexion", "header": [], "logout": "Déconnexion", "dashboard": "Espace Admin", "metaHeader": []}, "general": {"dropzone": "Déposez ou cliquez pour envoyer votre projet", "priceOnDemand": "Devis sur demande"}, "logoUrl": "https://xrmxsojjgvlabymrbqbk.supabase.co/storage/v1/object/public/alabri-images/img-1745327394125.webp", "seoTags": ["bâtiment", "construction", "rénovation", "entreprise générale"], "articles": {"label": "Article", "readMore": "Lire la suite"}, "error404": {"title": "Cette page n'existe pas", "subtitle": "Mais ne vous inquiétez pas, nous avons d'autres pages à explorer !", "returnArticles": "Voir les articles", "returnHomepage": "Retour à l'accueil", "returnProducts": "Voir les services"}, "metadata": {"url": "https://www.alabri-btp.com/fr", "image": {"alt": "Alabri BTP - Entreprise Générale du Bâtiment", "url": "https://www.alabri-btp.com/images/og-image-fr.jpg", "width": 1200, "height": 630}, "title": "Alabri BTP - Entreprise Générale du Bâtiment", "seoTags": ["bâtiment", "construction", "rénovation", "entreprise générale"], "canonical": "https://www.alabri-btp.com/fr", "languages": {"en": "https://www.alabri-btp.com/en", "fr": "https://www.alabri-btp.com/fr"}, "description": "Alabri BTP est spécialisée dans la construction, la rénovation et la gestion de projets immobiliers. Devis gratuit et accompagnement personnalisé."}, "products": {"back": "Voir tous les services", "label": "Projet", "custom": "Personnalisé", "readMore": "En savoir plus", "dimensions": "Caractéristiques", "priceLabel": "Devis", "redirectionTitle": "Vous recherchez un expert pour votre projet ?", "qualityFranckSabet": "Les projets réalisés par ALABRI BTP sont exécutés avec professionnalisme et respectent des standards de qualité élevés", "redirectionSubtitle": "Découvrez nos services et trouvez le savoir-faire qu'il vous faut pour réaliser votre rêve !"}, "compagnyName": "ALABRI BTP"}.`}
            `.trim()
            :
            `Translate this JSON into English without changing image URLs or links:\n\n${JSON.stringify(jsonBase, null, 2)}`;;

        await navigator.clipboard.writeText(prompt);
        if (locale === 'fr') {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } else {
            setCopiedEN(true);
            setTimeout(() => setCopiedEN(false), 2000);
        }
    };

    return (
        <Center style={{ height: '100vh', background: '#f9f9f9' }}>
            <Box
                bg="white"
                p="xl"
                style={{
                    borderRadius: '8px',
                    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                    width: 500,
                }}
            >
                <Title order={4} mb="md" ta="center" c="gray.7">
                    🎯 Généreration du site internet optimisé
                </Title>

                <Stack>
                    <TextInput
                        label="Nom de l'entreprise"
                        placeholder="ex: ALABRI BTP"
                        value={compagnyName}
                        onChange={(e) => setCompagnyName(e.currentTarget.value)}
                    />
                    <Textarea
                        label="Description de l'entreprise"
                        placeholder="Rénovation, BTP, expertise, etc."
                        value={description}
                        onChange={(e) => setDescription(e.currentTarget.value)}
                        autosize
                        minRows={3}
                    />
                    {/* @ts-ignore */}
                    <Dropzone imageUrl={logoUrl} setImageUrl={setLogoUrl} />

                    <Textarea
                        label="JSON de base FR (optionnel)"
                        placeholder="Collez ici un JSON de base si vous en avez un"
                        value={jsonBase}
                        onChange={(e) => setJsonBase(e.currentTarget.value)}
                        autosize
                        minRows={6}
                    />
                    <Tooltip label="Remplissez d'abord tous les champs" disabled={allFieldsFilled}>
                        <Button
                            variant="light"
                            onClick={() => handleCopyPrompt('fr')}
                            disabled={!allFieldsFilled}
                            color={copied ? 'teal' : 'gray'}
                        >
                            {copied ? '✅ Prompt FR copié' : '📋 Copier le prompt GPT (FR)'}
                        </Button>
                    </Tooltip>

                    <Textarea
                        label="JSON de base EN (optionnel)"
                        placeholder="Paste base EN JSON here if available"
                        value={jsonBaseEN}
                        onChange={(e) => setJsonBaseEN(e.currentTarget.value)}
                        autosize
                        minRows={6}
                    />

                    <Tooltip label="Remplissez d'abord tous les champs" disabled={allFieldsFilled}>
                        <Button
                            variant="light"
                            onClick={() => handleCopyPrompt('en')}
                            disabled={!allFieldsFilled}
                            color={copiedEN ? 'teal' : 'gray'}
                        >
                            {copiedEN ? '✅ Prompt EN copié' : '📋 Copier le prompt GPT (EN)'}
                        </Button>
                    </Tooltip>

                    <Button
                        onClick={handleGenerate}
                        loading={loading}
                        disabled={!allFieldsFilled}
                        color="gray"
                        variant="filled"
                    >
                        🚀 Générer & Enregistrer
                    </Button>
                </Stack>
            </Box>
        </Center>
    );
}
